import React, { useState, useRef, useEffect } from 'react';

const STORAGE_KEY = 'opoDocumentAccountantCLIPosition';
const DEFAULT_POSITION = { bottom: '24px', right: '24px' };

function loadPosition() {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s) {
      const p = JSON.parse(s);
      if (p && (typeof p.bottom === 'string' || typeof p.top === 'string')) return p;
    }
  } catch (_) {}
  return DEFAULT_POSITION;
}

function savePosition(position) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch (_) {}
}

function convertToPosition(clientX, clientY, rect, viewportWidth, viewportHeight) {
  const fromRight = viewportWidth - clientX - rect.width / 2;
  const fromBottom = viewportHeight - clientY - rect.height / 2;
  const fromLeft = clientX - rect.width / 2;
  const fromTop = clientY - rect.height / 2;
  const out = {};
  if (fromRight < fromLeft) {
    out.right = Math.max(10, Math.min(fromRight, viewportWidth - rect.width - 10)) + 'px';
  } else {
    out.left = Math.max(10, Math.min(fromLeft, viewportWidth - rect.width - 10)) + 'px';
  }
  if (fromBottom < fromTop) {
    out.bottom = Math.max(10, Math.min(fromBottom, viewportHeight - rect.height - 10)) + 'px';
  } else {
    out.top = Math.max(10, Math.min(fromTop, viewportHeight - rect.height - 10)) + 'px';
  }
  return out;
}

/**
 * Floating, draggable "Ask the Accountant" CLI for the document viewer.
 * Same API and behaviour as OPO page Accountant chat: history, data + layout/format edits.
 * Persists across sheet tabs. Calls onEditsApplied when the assistant reports applied edits or format so the sheet can refetch.
 * currentSheet: the sheet/tab the user is viewing (e.g. "SUMMARY", "FUNDING_LEDGER") so the backend can apply edits where they're looking.
 */
function FloatingAccountantCLI({ documentId, currentSheet, onEditsApplied }) {
  const [expanded, setExpanded] = useState(false);
  const [position, setPosition] = useState(DEFAULT_POSITION);
  const [query, setQuery] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [lastReply, setLastReply] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [clearChatLoading, setClearChatLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const historyScrollRef = useRef(null);
  const dragRef = useRef({ isDragging: false, hasMoved: false, startPos: { x: 0, y: 0 } });
  const positionRef = useRef(position);
  positionRef.current = position;

  useEffect(() => {
    setPosition(loadPosition());
  }, []);

  // Scroll history to bottom (latest message) when history loads or when new content appears
  useEffect(() => {
    if (!expanded) return;
    const el = historyScrollRef.current;
    if (!el) return;
    const t = requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(t);
  }, [expanded, history, historyLoading, streamingText, lastReply]);

  const fetchHistory = () => {
    setHistoryLoading(true);
    fetch('/api/opo/auditor/history?subAgent=accountant')
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to load history')))
      .then((d) => setHistory(d.history || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };

  useEffect(() => {
    if (expanded) fetchHistory();
  }, [expanded]);

  const sendMessage = async () => {
    const msg = query.trim();
    if (!msg || streaming) return;
    setQuery('');
    setStreaming(true);
    setStreamingText('');
    setLastReply('');
    try {
      const body = { message: msg, subAgent: 'accountant' };
      if (documentId != null && String(documentId).trim()) {
        body.documentId = String(documentId).trim();
      }
      if (currentSheet && String(currentSheet).trim()) {
        body.currentSheet = String(currentSheet).trim();
      }
      const res = await fetch('/api/opo/auditor/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok || !res.body) {
        setLastReply('Error: ' + (res.statusText || 'No response'));
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setStreamingText(full);
      }
      setLastReply(full);
      setStreamingText('');
      fetchHistory();
      const appliedDataOrFormat = /applied|saved|format|alignment|edit\(s\)/i.test(full);
      if (typeof onEditsApplied === 'function' && appliedDataOrFormat) {
        onEditsApplied();
      }
    } catch (e) {
      setLastReply('Error: ' + (e.message || 'Request failed'));
    } finally {
      setStreaming(false);
    }
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    const drag = dragRef.current;
    drag.isDragging = true;
    drag.hasMoved = false;
    drag.startPos = { x: e.clientX, y: e.clientY };
    setIsDragging(true);
    const onMove = (e) => {
      if (!drag.isDragging) return;
      const dx = Math.abs(e.clientX - drag.startPos.x);
      const dy = Math.abs(e.clientY - drag.startPos.y);
      if (dx > 5 || dy > 5) {
        drag.hasMoved = true;
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const pos = convertToPosition(e.clientX, e.clientY, rect, window.innerWidth, window.innerHeight);
          setPosition(pos);
        }
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      drag.isDragging = false;
      setIsDragging(false);
      if (drag.hasMoved) setTimeout(() => savePosition(positionRef.current), 10);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    const drag = dragRef.current;
    drag.isDragging = true;
    drag.hasMoved = false;
    drag.startPos = { x: touch.clientX, y: touch.clientY };
    setIsDragging(true);
    const onMove = (e) => {
      if (!drag.isDragging) return;
      e.preventDefault();
      const t = e.touches[0];
      const dx = Math.abs(t.clientX - drag.startPos.x);
      const dy = Math.abs(t.clientY - drag.startPos.y);
      if (dx > 5 || dy > 5) {
        drag.hasMoved = true;
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const pos = convertToPosition(t.clientX, t.clientY, rect, window.innerWidth, window.innerHeight);
          setPosition(pos);
        }
      }
    };
    const onEnd = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      drag.isDragging = false;
      setIsDragging(false);
      if (drag.hasMoved) setTimeout(() => savePosition(positionRef.current), 10);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  const handleHeaderClick = () => {
    if (dragRef.current.hasMoved) return;
    setExpanded((e) => !e);
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded(false);
  };

  const handleClearChat = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (clearChatLoading) return;
    setClearChatLoading(true);
    fetch('/api/opo/auditor/accountant/clear-chat', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('Failed to clear')))
      .then(() => {
        setHistory([]);
        setLastReply('');
        setStreamingText('');
      })
      .catch(() => {})
      .finally(() => {
        setClearChatLoading(false);
      });
  };

  const baseBox = {
    position: 'fixed',
    ...position,
    zIndex: 99999,
    minWidth: 280,
    maxWidth: 420,
    background: '#1e293b',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const header = {
    padding: '10px 14px',
    cursor: isDragging ? 'grabbing' : 'grab',
    userSelect: 'none',
    touchAction: 'none',
    background: 'rgba(96, 165, 250, 0.12)',
    borderBottom: expanded ? '1px solid rgba(255,255,255,0.1)' : 'none',
    color: '#e2e8f0',
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  };

  return (
    <div ref={containerRef} style={baseBox} className="floating-accountant-cli">
      <div
        style={header}
        onMouseDown={handleDragStart}
        onTouchStart={handleTouchStart}
        onClick={handleHeaderClick}
        title="Drag to move · Click to expand/collapse"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ opacity: 0.9 }}>📊</span>
          <span>Ask the Accountant…</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {expanded && (
            <button
              type="button"
              onClick={handleMinimize}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              title="Minimize"
              style={{
                flexShrink: 0,
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                lineHeight: 1,
                color: '#94a3b8',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 6,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              −
            </button>
          )}
          <button
            type="button"
            onClick={handleClearChat}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          disabled={clearChatLoading}
          title="Archive conversation for audit and clear chat"
          style={{
            flexShrink: 0,
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: 600,
            color: '#94a3b8',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 6,
            cursor: clearChatLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {clearChatLoading ? 'Clearing…' : 'Clear Chat'}
        </button>
        </span>
      </div>
      {expanded && (
        <>
          <div ref={historyScrollRef} style={{ padding: 12, flex: 1, minHeight: 80, maxHeight: 260, overflowY: 'auto', fontSize: '12px', color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {historyLoading ? (
              <span style={{ color: '#94a3b8' }}>Loading history…</span>
            ) : history.length === 0 && !streamingText && !lastReply ? (
              <span style={{ color: '#94a3b8' }}>Ask to edit data or layout (e.g. "Set B2 to 100", "Align Founder Contribution to the right").</span>
            ) : (
              <>
                {history.map((m, i) => (
                  <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '95%' }}>
                    <span style={{ fontWeight: 600, color: m.role === 'user' ? '#93c5fd' : '#86efac', marginRight: 6 }}>{m.role === 'user' ? 'You' : 'Accountant'}:</span>
                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</span>
                  </div>
                ))}
                {streamingText && (
                  <div style={{ alignSelf: 'flex-start' }}>
                    <span style={{ fontWeight: 600, color: '#86efac', marginRight: 6 }}>Accountant:</span>
                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{streamingText}<span style={{ opacity: 0.8 }}>▌</span></span>
                  </div>
                )}
                {lastReply && !streamingText && (
                  <div style={{ alignSelf: 'flex-start' }}>
                    <span style={{ fontWeight: 600, color: '#86efac', marginRight: 6 }}>Accountant:</span>
                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{lastReply}</span>
                  </div>
                )}
              </>
            )}
          </div>
          <div style={{ padding: '8px 12px 12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Type instruction… (Enter to send)"
              disabled={streaming}
              rows={2}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                fontSize: '13px',
                color: '#e2e8f0',
                background: 'rgba(15,23,42,0.8)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8,
                resize: 'vertical',
                minHeight: 52,
              }}
            />
            <button
              type="button"
              onClick={sendMessage}
              disabled={streaming || !query.trim()}
              style={{
                marginTop: 8,
                width: '100%',
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#0f172a',
                background: streaming ? '#64748b' : '#60a5fa',
                border: 'none',
                borderRadius: 8,
                cursor: streaming ? 'not-allowed' : 'pointer',
              }}
            >
              {streaming ? 'Sending…' : 'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default FloatingAccountantCLI;
